import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetch from "auth/FetchInterceptor";
import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { AUTH_TOKEN } from "constants/AuthConstant";

// ---------------- GET ALL CARS ----------------
export const getAllCars = createAsyncThunk(
  "car/getAllCars",
  async (params, { rejectWithValue }) => {
    try {
      const {
        page = 1,
        limit = 5,
        nom,
        modelCarIds,
        minPrix,
        maxPrix,
      } = params;

      const queryParams = new URLSearchParams();

      queryParams.append("page", page);
      queryParams.append("limit", limit);

      if (nom) queryParams.append("nom", nom);
      if (minPrix) queryParams.append("minPrix", minPrix);
      if (maxPrix) queryParams.append("maxPrix", maxPrix);

      if (modelCarIds && Array.isArray(modelCarIds) && modelCarIds.length > 0) {
        queryParams.append("modelCarIds", modelCarIds.join(","));
      }

      const response = await fetch({
        url: `/car/all?${queryParams.toString()}`,
        method: "get",
      });
      // console.log(response);
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors du chargement"
      );
    }
  }
);

// ---------------- GET CAR BY ID ----------------
export const getCarById = createAsyncThunk(
  "car/getCarById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetch({
        url: `/car/getById/${id}`,
        method: "get",
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur lors du chargement du véhicule"
      );
    }
  }
);

// ---------------- ADD CAR ----------------
export const addCar = createAsyncThunk(
  "car/addCar",
  async (data, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Add regular fields
      Object.keys(data).forEach((key) => {
        if (key !== "image" && key !== "secondaryImages") {
          formData.append(key, data[key]);
        }
      });

      // Add main image
      if (data.image) {
        formData.append("image", data.image);
      }

      // Add secondary images
      if (data.secondaryImages && data.secondaryImages.length > 0) {
        data.secondaryImages.forEach((file, index) => {
          formData.append("secondaryImages", file);
        });
      }

      const response = await axios.post(
        `${API_BASE_URL}/car/create`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      const status = error?.response?.status;

      if (status === 400) {
        return rejectWithValue("Ce véhicule existe déjà.");
      }

      return rejectWithValue("Erreur inconnue");
    }
  }
);

// ---------------- DELETE CAR ----------------
export const deleteCar = createAsyncThunk(
  "car/deleteCar",
  async (id, { rejectWithValue }) => {
    try {
      await fetch({ url: `/car/delete/${id}`, method: "delete" });
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.error || "Erreur à la suppression"
      );
    }
  }
);

// ---------------- UPDATE CAR ----------------
export const updateCar = createAsyncThunk(
  "car/updateCar",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      // Add regular fields
      Object.keys(data).forEach((key) => {
        if (
          key !== "image" &&
          key !== "secondaryImages" &&
          key !== "existingImages" &&
          key !== "existingMainImage"
        ) {
          formData.append(key, data[key]);
        }
      });

      if (data.image) {
        if (typeof data.image === "string") {
          // Existing image path
          formData.append("existingMainImage", data.image);
        } else {
          // New image file
          formData.append("image", data.image);
        }
      }

      if (data.secondaryImages && data.secondaryImages.length > 0) {
        const existingImages = [];
        const newImages = [];

        data.secondaryImages.forEach((item) => {
          if (typeof item === "string") {
            // Existing image path
            existingImages.push(item);
          } else {
            // New image file
            newImages.push(item);
          }
        });

        // Send existing images as JSON array
        if (existingImages.length > 0) {
          formData.append(
            "existingSecondaryImages",
            JSON.stringify(existingImages)
          );
        }

        // Send new images as files
        newImages.forEach((file) => {
          formData.append("secondaryImages", file);
        });
      }

      // console.log(formData);
      const response = await axios.put(
        `${API_BASE_URL}/car/updateById/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
          },
        }
      );

      return { id, response: response.data };
    } catch (error) {
      const status = error?.response?.status;

      if (status === 400) {
        return rejectWithValue("Ce véhicule existe déjà.");
      }

      return rejectWithValue("Erreur inconnue");
    }
  }
);

// ---------------- ASSIGN CAR TO CLIENT ----------------

export const assignCarToClient = createAsyncThunk(
  "car/assignCarToClient",
  async (data, { rejectWithValue }) => {
    try {
      // On appelle axios brut → on a la réponse complète
      const response = await axios.post(
        `${API_BASE_URL}/car/assign-to-client`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
          },
        }
      );

      // console.log("✅ Réponse brute :", response);
      return response.data;
    } catch (error) {
      // console.error("❌ Erreur API assignCarToClient :", error);

      const status = error?.response?.status;

      if (status === 400) {
        return rejectWithValue("Cette voiture est déjà assignée à un client.");
      }
      if (status === 404) {
        return rejectWithValue("Ce client n'existe pas.");
      }

      return rejectWithValue(error?.response?.data?.error || "Erreur inconnue");
    }
  }
);

const carSlice = createSlice({
  name: "car",
  initialState: {
    cars: [],
    selectedCar: null, // Added selectedCar for car details
    total: 0,
    page: 1,
    totalPages: 1,
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetCarsState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearSelectedCar: (state) => {
      state.selectedCar = null;
    },
  },
  extraReducers: (builder) => {
    // ----- GET ALL -----
    builder
      .addCase(getAllCars.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllCars.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.cars = payload.data;
        state.total = payload.total;
        state.page = payload.page;
        state.totalPages = payload.totalPages;
      })
      .addCase(getAllCars.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // ----- GET BY ID -----
    builder
      .addCase(getCarById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCarById.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.selectedCar = payload.data;
      })
      .addCase(getCarById.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });

    // ----- ADD -----
    builder
      .addCase(addCar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(addCar.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(addCar.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      });

    // ----- DELETE -----
    builder
      .addCase(deleteCar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCar.fulfilled, (state, { payload: deletedId }) => {
        state.loading = false;
        state.success = true;
        state.cars = state.cars.filter((m) => m.id !== deletedId);
        state.total -= 1;
      })
      .addCase(deleteCar.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      });

    // ----- UPDATE -----
    builder
      .addCase(updateCar.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCar.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(updateCar.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
        state.success = false;
      });

    // ----- ASSIGN -----
    builder
      .addCase(assignCarToClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(assignCarToClient.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(assignCarToClient.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Erreur lors de l'assignation de la voiture";
      });
  },
});

export const { resetCarsState, clearSelectedCar } = carSlice.actions;
export default carSlice.reducer;
